import { Button, Upload, message } from 'antd';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { useFileUpload } from 'hooks';
import { InboxOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import client from 'apolloClient';
import { CREATE_INVOICE } from 'gql/appointments/mutation';
import { GET_APPOINTMENT_ITEM } from 'gql/appointments/queries';
import { InvoiceTypes } from 'constants/invoice';

import { showErrorMessage } from 'utils/showErrorMessage';
import { Loading } from 'components/ui';

interface IProps {
  onClose(): void;
  appointmentId: string;
}

const UploadInvoice: FC<IProps> = ({ appointmentId, onClose }) => {
  const { t } = useTranslation();
  const [percent, setPercent] = useState(0);
  const [showFileList, setShowFileList] = useState(true);
  const {
    result: file,
    upload,
    onCancel
  } = useFileUpload({
    handleResult: true
  });

  const [createInvoice, { loading: isCreating }] = useMutation(CREATE_INVOICE, {
    onCompleted() {
      message.success(t('messages.successfully_created'));
      client.refetchQueries({ include: [GET_APPOINTMENT_ITEM] });
      onClose();
    },
    onError: err => showErrorMessage(err)
  });

  const onUploadFile = async (e: UploadRequestOption) => {
    try {
      setShowFileList(false);
      const res = await upload(e.file as File, {
        onUploadProgress({ progress: value }) {
          setPercent(value || 0);

          if (value === 100) {
            setTimeout(() => {
              setPercent(0);
            }, 100);
          }
        }
      });

      e.onSuccess?.(res);
    } catch (err) {
      e.onError?.(err as ProgressEvent<EventTarget>);
    } finally {
      setShowFileList(true);
    }
  };

  const onSubmit = () => {
    createInvoice({
      variables: {
        input: {
          appointment_id: appointmentId,
          type: InvoiceTypes.file,
          file
        }
      }
    });
  };

  const isUploading = percent > 0;

  return (
    <div style={{ position: 'relative' }}>
      <Upload.Dragger
        name="file"
        customRequest={onUploadFile}
        onRemove={f => onCancel(f.uid)}
        maxCount={1}
        disabled={isUploading}
        showUploadList={showFileList}
        listType="picture"
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t('sessions.upload_file')}</p>
        <p className="ant-upload-hint">{t('messages.only_images_and_pdf')}</p>
      </Upload.Dragger>
      <div style={{ paddingTop: 16 }}>
        {isUploading ? (
          <Loading type="static" percent={percent} />
        ) : (
          <Button
            type="primary"
            size="large"
            onClick={onSubmit}
            loading={isCreating}
            disabled={!file}
            block
          >
            {t('common.submit')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default UploadInvoice;
